import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

import { OnboardingDetailsDtoOnboardingStatusEnum } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { mailboxSyncInProgressAtom } from '@/contexts/userContext/MailboxSync.context.ts';
import { useGetUserOnboardingDetails } from '@/contexts/userContext/util/useGetUserOnboardingDetails.util.ts';
import { useOnboardUser } from '@/contexts/userContext/util/useOnboardUser.util.ts';
import { returnPromise } from '@/utils/returnPromise.util.ts';
import { refreshThreadList } from '@/views/emails/util/threadListCache.util.ts';

const SYNC_POLL_INTERVAL_MS = 5_000;
const SYNC_WAIT_LIMIT_MS = 15 * 60_000;

type Return = {
  synchronizeMailbox: (userId: string) => Promise<void>;
};

export const useSynchronizeMailbox = (): Return => {
  const queryClient = useQueryClient();
  const setSyncInProgress = useSetAtom(mailboxSyncInProgressAtom);
  const { getOnboardingDetails } = useGetUserOnboardingDetails();
  const { onboardUser } = useOnboardUser();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const waitForCompletion = async (userId: string): Promise<boolean> => {
    const startedAt = Date.now();
    while (isMountedRef.current && Date.now() - startedAt < SYNC_WAIT_LIMIT_MS) {
      await returnPromise(undefined, SYNC_POLL_INTERVAL_MS);
      const { onboardingStatus } = await getOnboardingDetails(userId);
      if (onboardingStatus === OnboardingDetailsDtoOnboardingStatusEnum.Completed) {
        return true;
      }
    }
    return false;
  };

  const refreshMailbox = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QueryKeyEnum.EMAILS.FOLDERS] }),
      queryClient.invalidateQueries({ queryKey: [QueryKeyEnum.EMAILS.FOLDERS_STATE] }),
      queryClient.invalidateQueries({ queryKey: [QueryKeyEnum.DASHBOARD.EMAILS] }),
      refreshThreadList(queryClient),
    ]);
  };

  const synchronizeMailbox = async (userId: string) => {
    try {
      const { onboardingStatus } = await getOnboardingDetails(userId);
      if (onboardingStatus === OnboardingDetailsDtoOnboardingStatusEnum.Completed) {
        return;
      }
      setSyncInProgress(true);
      if (onboardingStatus !== OnboardingDetailsDtoOnboardingStatusEnum.InProgress) {
        await onboardUser(userId);
      }
      if (await waitForCompletion(userId)) {
        await refreshMailbox();
      }
    } catch {
    } finally {
      setSyncInProgress(false);
    }
  };

  return { synchronizeMailbox };
};
